/**
 * Runtime assertion utilities for establishing and maintaining system invariants
 * These assertions help build more robust systems by validating critical conditions
 * 
 * This module provides runtime assertions with custom error types for
 * domain-specific validators and maintaining backward compatibility
 */

// Re-export the error type for backward compatibility
export class AssertionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AssertionError';
  }
}

/**
 * Assert that a condition is true
 * @param condition - The condition to check
 * @param message - Error message if assertion fails
 * @throws {AssertionError} If condition is false
 */
export function assert(condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new AssertionError(message);
  }
}

/**
 * Assert that a value is defined (not null or undefined)
 * @param value - The value to check
 * @param name - Name of the value for error message
 * @throws {AssertionError} If value is null or undefined
 */
export function assertDefined<T>(value: T | null | undefined, name: string): asserts value is T {
  assert(value !== null && value !== undefined, `${name} must be defined`);
}

/**
 * Assert that a string is not empty
 * @param value - The string to check
 * @param name - Name of the value for error message
 * @throws {AssertionError} If string is empty or only whitespace
 */
export function assertNotEmpty(value: string | null | undefined, name: string): asserts value is string {
  assertDefined(value, name);
  assert(value.trim().length > 0, `${name} must not be empty`);
}

/**
 * Assert that a number is within a range
 * @param value - The number to check
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (inclusive)
 * @param name - Name of the value for error message
 * @throws {AssertionError} If value is outside range
 */
export function assertInRange(value: number, min: number, max: number, name: string): void {
  assert(value >= min && value <= max, `${name} must be between ${min} and ${max}, got ${value}`);
}

/**
 * Assert that an array has elements
 * @param array - The array to check
 * @param name - Name of the array for error message
 * @throws {AssertionError} If array is empty
 */
export function assertNotEmptyArray<T>(array: T[] | null | undefined, name: string): asserts array is T[] {
  assertDefined(array, name);
  assert(array.length > 0, `${name} must not be empty`);
}

/**
 * Assert that an array has a specific length
 * @param array - The array to check
 * @param expectedLength - Expected length
 * @param name - Name of the array for error message
 * @throws {AssertionError} If array length doesn't match
 */
export function assertArrayLength<T>(array: T[], expectedLength: number, name: string): void {
  assert(array.length === expectedLength, `${name} must have length ${expectedLength}, got ${array.length}`);
}

/**
 * Assert that a value matches a pattern
 * @param value - The string to check
 * @param pattern - Regular expression pattern
 * @param name - Name of the value for error message
 * @throws {AssertionError} If value doesn't match pattern
 */
export function assertPattern(value: string, pattern: RegExp, name: string): void {
  assert(pattern.test(value), `${name} must match pattern ${pattern}`);
}

/**
 * Assert that an object has required properties
 * @param obj - The object to check
 * @param properties - Array of required property names
 * @param name - Name of the object for error message
 * @throws {AssertionError} If any property is missing
 */
export function assertHasProperties<T extends object>(
  obj: T,
  properties: (keyof T)[],
  name: string
): void {
  for (const prop of properties) {
    assert(prop in obj, `${name} must have property '${String(prop)}'`);
  }
}

/**
 * Assert that a value is one of allowed values
 * @param value - The value to check
 * @param allowedValues - Array of allowed values
 * @param name - Name of the value for error message
 * @throws {AssertionError} If value is not in allowed values
 */
export function assertOneOf<T>(value: T, allowedValues: readonly T[], name: string): void {
  assert(
    allowedValues.includes(value),
    `${name} must be one of [${allowedValues.join(', ')}], got ${value}`
  );
}

/**
 * Assert that a URL is valid
 * @param url - The URL string to check
 * @param name - Name of the value for error message
 * @throws {AssertionError} If URL is invalid
 */
export function assertValidUrl(url: string, name: string): void {
  try {
    new URL(url);
  } catch {
    throw new AssertionError(`${name} must be a valid URL`);
  }
}

/**
 * Assert that an email is valid
 * @param email - The email to check
 * @param name - Name of the value for error message
 * @throws {AssertionError} If email is invalid
 */
export function assertValidEmail(email: string, name: string): void {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  assertPattern(email, emailPattern, name);
}

/**
 * Assert state transition is valid
 * @param currentState - Current state
 * @param newState - New state to transition to
 * @param allowedTransitions - Map of allowed state transitions
 * @throws {AssertionError} If transition is not allowed
 */
export function assertValidStateTransition<T extends string>(
  currentState: T,
  newState: T,
  allowedTransitions: Record<T, T[]>
): void {
  const allowed = allowedTransitions[currentState] || [];
  assert(
    allowed.includes(newState),
    `Invalid state transition from '${currentState}' to '${newState}'`
  );
}